/*
╔══════════════════════════════════╗          ╔══════════════════════════════╗
║ previewRenderer                  ╟─────┬───→║ spreadRenderer               ║
╟──────────────────────────────────╢     │    ╟──────────────────────────────╢
║ updatePreviewAsync               ║     │    ║ getRenderResourcesAsync      ║
║ getRenderSize                    ║     │    ║ renderSpread                 ║
║ getRenderToSpreadTransform       ║     │    ║ getRenderSize                ║
║ applyTransform                   ║     │    ║ applyTransform               ║
╚══════════════════════════════════╝     │    ╚══════════════════════════════╝
                                         │
                                         │    ╔══════════════════════════════╗
                                         └───→║ adornerRenderer              ║
                                              ╟──────────────────────────────╢
                                              ║ getRenderResourcesAsync      ║
                                              ║ renderAdorner                ║
                                              ║ getRenderSize                ║
                                              ║ getRenderToSpreadTransform   ║
                                              ║ applyTransform               ║
                                              ╚══════════════════════════════╝
*/
import { Transform } from "../../../transform";
import { IAdornerRenderer } from "./adorner-renderer";
import { ISpreadRenderer } from "./spread-renderer";

import { SceneGraphNode } from "../../../scene-graph";
import "./preview-renderer.css";

/*
---------------------------------------------------------------------------
Scene graph:
---------------------------------------------------------------------------
preview                        : Root of the preview
└─ adorner                     : To Adorner DIP coordinates (= unit transform when no adorner is available)
└─ spread                      : To spread DIP
----------------------------------------------------------------------------
*/
export interface IPreviewSceneGraph {
	/** Root of the preview */
	preview: SceneGraphNode;
	/** From what is rendered by the adorner renderer to adorner DIP coordinates (= unit transform when no adorner is available) */
	adorner: SceneGraphNode;
	/** From what is rendered by the spread renderer to the spread DIP */
	spread: SceneGraphNode;
}

/**
 * The preview render is a combination of Spread and Adorner renderer
 */
export interface IPreviewRenderer {
	readonly spread: eXpress.core.Spread;
	readonly el: HTMLDivElement;

	getRenderSize(): eXpress.math.IVector;
	/** Load spread resources (Adorner, images, fonts, Backgrounds, ...) and update DOM when ready */
	updatePreviewAsync(previewMode: boolean, topZone?: eXpress.core.IZone): Promise<void>;
	getZonesAtPoint(point: eXpress.math.IPoint): eXpress.core.IZone[];
	applyTransform(transform: Transform): void;
	setActive(isActive: boolean): void;
	sceneGraph: IPreviewSceneGraph;
	dispose(): void;
}

export interface IPreviewRendererDeps {
	client: Pick<eXpress.core.IClient, "logErrorAsync">;
	spreadRenderer: ISpreadRenderer;
	adornerRenderer?: IAdornerRenderer | null | undefined;
	log?: eXpress.ILogger;
}

export function createPreviewRenderer(spread: eXpress.core.Spread, deps: IPreviewRendererDeps) {
	const el = document.createElement("div");
	el.classList.add("preview");

	const { adornerRenderer, log } = deps;

	/** A function to throw an exception when disposed */
	function getSpreadRenderer() {
		if (!deps.spreadRenderer.isDisposed) return deps.spreadRenderer;
		throw new eXpress.GeneralException("DisposedException", "SpreadRenderer is disposed");
	}

	const previewSceneNode = new SceneGraphNode("preview", null);
	const adornerDipSceneNode = new SceneGraphNode("adorner", previewSceneNode);
	const spreadDipSceneNode = new SceneGraphNode("spread", previewSceneNode);
	const sceneGraph: IPreviewSceneGraph = {
		preview: previewSceneNode,
		adorner: adornerDipSceneNode,
		spread: spreadDipSceneNode,
	};

	// Add spread element
	getSpreadRenderer().el.style.position = "absolute";
	el.appendChild(getSpreadRenderer().el);

	// Add adorner element
	if (adornerRenderer) {
		adornerRenderer.el.style.position = "absolute";
		el.appendChild(adornerRenderer.el);
	}

	function updatePreviewAsync(previewMode: boolean, topZone?: eXpress.core.IZone) {
		el.classList.add("preview--loading");
		el.classList.remove("preview--error");

		// Load resources
		const loadSpreadPromise = getSpreadRenderer().getRenderResourcesAsync(spread);
		const loadAdornerPromise = adornerRenderer
			? adornerRenderer.getRenderResourcesAsync(spread.getAdorner())
			: Promise.resolve(undefined);

		// Load Render Resources
		const updatePreviewPromise = Promise.all([loadSpreadPromise, loadAdornerPromise])
			// Render
			.then(([spreadResources, adornerResources]) => {
				// Async, check if renders are disposed (still visible)
				if (!el || deps.spreadRenderer.isDisposed) return;

				adornerRenderer?.renderAdorner(adornerResources);

				getSpreadRenderer().renderSpread(
					spreadResources,
					previewMode,
					{ opacity: adornerResources?.adorner?.image.opacity },
					topZone,
				);

				el.classList.remove("preview--loading");
			})
			.catch((err: unknown) => {
				if (!el) return;
				if (err instanceof eXpress.OperationAbortedWarning) return;
				el.classList.remove("preview--loading");
				el.classList.add("preview--error");
				throw err;
			});

		// Set update sizes in sceneGraph
		sceneGraph.adorner.setTransform(
			adornerRenderer?.getRenderToAdornerTransform().inverse() || Transform.unit,
		);

		/** Because spread size doesn't change, we get it only once */
		const spreadRenderSize = getSpreadRenderer().getRenderSize(spread);

		// Update spread DIP transform
		const dipToRenderedSpreadTransform = new Transform({
			scale: spreadRenderSize.width / spread.width,
		});

		const renderedSpreadToAdornerTransform =
			adornerRenderer?.getRenderToProjectionTransform(spreadRenderSize).inverse() ||
			Transform.unit;

		sceneGraph.spread.setTransform(
			adornerRenderer
				? dipToRenderedSpreadTransform.mul(renderedSpreadToAdornerTransform)
				: renderedSpreadToAdornerTransform,
		);

		return updatePreviewPromise;
	}

	function getZonesAtPoint(
		/** 0, 0 is the topleft corner of the preview, the preview is centered in preview area */
		pointInPreviewSpace: eXpress.math.IPoint,
	): eXpress.core.IZone[] {
		// Hittest is done in spread image coordinates (not dip)
		const pointOnSpread = sceneGraph.preview
			.getTransformTo(sceneGraph.spread)
			.transformPoint(pointInPreviewSpace);

		return getSpreadRenderer().getZonesAtPoint(pointOnSpread, spread);
	}

	/** Size of the spread/adorner (is not the image size) */
	function getRenderSize() {
		const adorner = spread.getAdorner();
		if (adorner && adornerRenderer) {
			return adornerRenderer.getRenderSize();
		}
		return getSpreadRenderer().getRenderSize(spread);
	}

	/** Apply transform on adorner and spread */
	function applyTransform(transform: Transform) {
		// Skip transforms for a spread that is not in the definition
		// because spread.width requires a container
		// This did happen for example on a reset definition, zone is deselected, UI updates = applyTransform before new items are set
		if (!spread.hasOwner()) {
			log?.warn(`Rerendering of dangling spread: ${spread.toString()}`);
			return;
		}

		const previewToSpreadRenderTransform = adornerRenderer
			? adornerRenderer
					.getRenderToProjectionTransform(getSpreadRenderer().getRenderSize(spread))
					.inverse()
					.mul(transform)
			: getSpreadRenderer().getRenderToSpreadTransform(spread).mul(transform);

		getSpreadRenderer().applyTransform(previewToSpreadRenderTransform);
		adornerRenderer?.applyTransform(transform);
	}

	/** Set the active state of the preview */
	function setActive(isActive: boolean) {
		el.classList.toggle("preview--active", isActive);
	}

	const item: IPreviewRenderer = {
		spread,
		el,

		getRenderSize,
		updatePreviewAsync,
		getZonesAtPoint,
		applyTransform,
		setActive,
		sceneGraph,

		dispose() {
			if (adornerRenderer) {
				adornerRenderer.el.remove();
			}
			deps.spreadRenderer.el.remove();
			deps.spreadRenderer.dispose();
		},
	};
	return item;
}
